import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable, fromEvent, merge } from "rxjs";
import { map, startWith, distinctUntilChanged } from "rxjs/operators";
import { LoggerService } from "../../ancestors/logger.service";

/**
 * Servizio per il rilevamento dello stato online/offline dell'applicazione
 * Monitora lo stato della connessione di rete e fornisce un Observable per reagire ai cambiamenti
 */
@Injectable({
  providedIn: "root"
})
export class OfflineDetectionService {
  private logger = inject(LoggerService);
  
  private isOnlineSubject = new BehaviorSubject<boolean>(navigator.onLine);

  /**
   * Observable che emette true quando l'utente è online, false quando è offline
   */
  public readonly isOnline$: Observable<boolean> = this.isOnlineSubject.asObservable();

  /**
   * Observable che emette true quando l'utente è offline, false quando è online
   */
  public readonly isOffline$: Observable<boolean> = this.isOnline$.pipe(
    map(isOnline => !isOnline)
  );

  constructor() {
    this.initializeOfflineDetection();
  }

  /**
   * Inizializza il sistema di rilevamento offline/online
   */
  private initializeOfflineDetection(): void {
    // Ascolta gli eventi di connessione dal browser
    const online$ = fromEvent(window, "online").pipe(map(() => true));
    const offline$ = fromEvent(window, "offline").pipe(map(() => false));

    // Combina gli eventi e inizia con lo stato corrente
    merge(online$, offline$)
      .pipe(
        startWith(navigator.onLine),
        distinctUntilChanged()
      )
      .subscribe(isOnline => {
        this.logger.debug(`Network status changed: ${isOnline ? "Online" : "Offline"}`);
        this.isOnlineSubject.next(isOnline);
      });

    // Log dello stato iniziale
    this.logger.debug(`Initial network status: ${navigator.onLine ? "Online" : "Offline"}`);
  }

  /**
   * Getter per ottenere lo stato corrente della connessione
   * @returns true se online, false se offline
   */
  public get isOnline(): boolean {
    return this.isOnlineSubject.value;
  }

  /**
   * Getter per ottenere lo stato corrente offline
   * @returns true se offline, false se online
   */
  public get isOffline(): boolean {
    return !this.isOnlineSubject.value;
  }

  /**
   * Testa la connessione effettuando una richiesta di rete
   * Utile per verificare la connessione reale oltre al browser API
   * @param testUrl - URL da testare (default: fetch a un'immagine piccola)
   * @param timeoutMs - Timeout in millisecondi (default: 5000)
   * @returns Promise che risolve true se la connessione è disponibile
   */
  async testNetworkConnection(
    testUrl: string = "/favicon.ico", 
    timeoutMs: number = 5000
  ): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(testUrl, {
        method: "HEAD",
        cache: "no-cache",
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      
      const isConnected = response.ok;
      this.logger.debug(`Network test result: ${isConnected ? "Connected" : "Failed"}`);
      
      return isConnected;
    } catch (error) {
      this.logger.debug(`Network test failed: ${error instanceof Error ? error.message : String(error)}`);
      return false;
    }
  }

  /**
   * Forza un aggiornamento dello stato di connessione
   * Utile per test manuali o quando si sospetta che lo stato sia obsoleto
   */
  async forceUpdateConnectionStatus(): Promise<void> {
    const actualStatus = await this.testNetworkConnection();
    const browserStatus = navigator.onLine;

    this.logger.debug(`Force update - Browser: ${browserStatus}, Actual: ${actualStatus}`);

    // Usa il test di rete se diverso dal browser status
    const finalStatus = browserStatus && actualStatus;
    
    if (finalStatus !== this.isOnlineSubject.value) {
      this.logger.debug(`Forced status update: ${finalStatus ? "Online" : "Offline"}`);
      this.isOnlineSubject.next(finalStatus);
    }
  }

  /**
   * Pulizia delle risorse (se necessario)
   */
  ngOnDestroy(): void {
    this.isOnlineSubject.complete();
  }
}
